import { useState } from 'react';
import { CheckCircleOutlined } from '@mui/icons-material';
import {
	FormActions,
	FormCard,
	FormField,
	FormInput,
	FormSelect,
	FormSectionTitle,
} from '@practium/ui';
import { useCalagemTab } from '../../hooks/useCalagemTab';
import { SimpleDataTable } from '../Tables/SimpleDataTable/SimpleDataTable';
import { RESULT_TABLE_COLUMNS, mapResultTableRows } from '../../utils/correctionTableMapper';
import {
	CALAGEM_METODO_OPTIONS,
	APLICACAO_FORMA_OPTIONS,
	METODO_VALUE_CONFIG,
	DOSE_SECUNDARIA_LABEL,
	CORRETIVO_OPTIONS,
	getOptionLabel,
} from '../../constants/correction.constants';
import { PanelActions } from '../PanelActions/PanelActions';
import styles from './TabPanel.module.css';

interface CalagemTabProps {
	canEdit?: boolean;
}

export const CalagemTab = ({ canEdit = true }: CalagemTabProps) => {
	const {
		form,
		ajuste,
		result,
		hasCalculated,
		isCalculating,
		isSaving,
		errors,
		set,
		setAjuste,
		handleCalculate,
		handleSave,
		handleClear,
		corretivoOptions,
		handleAdjustDose,
		isAdjusting,
	} = useCalagemTab();

	const [isSaved, setIsSaved] = useState(() => hasCalculated && !!form.corretivo);

	const corretivoLabelOptions = [...corretivoOptions, ...CORRETIVO_OPTIONS];
	const metodoConfig = form.metodo ? METODO_VALUE_CONFIG[form.metodo] : null;
	const doseSecLabel = form.forma ? DOSE_SECUNDARIA_LABEL[form.forma] : '';
	const showLargura = form.forma === 'sulco' || form.forma === 'faixa' || form.forma === 'cova';
	const showComprimento = form.forma === 'cova';

	const handleSaveAndView = async () => {
		const saved = await handleSave();
		if (saved) setIsSaved(true);
	};

	if (isSaved) {
		return (
			<div className={styles.panel}>
				<FormCard icon={<CheckCircleOutlined />} title="Calagem">
					<div className={styles.form}>
						<div className={styles.section}>
							<FormSectionTitle>Cálculo</FormSectionTitle>
							<div className={styles.sectionFields}>
								<FormField label="Corretivo">
									<span className={styles.viewText}>
										{getOptionLabel(corretivoLabelOptions, form.corretivo)}
									</span>
								</FormField>
								<div className={styles.row2}>
									<FormField label="Método de cálculo">
										<span className={styles.viewText}>
											{getOptionLabel(CALAGEM_METODO_OPTIONS, form.metodo)}
										</span>
									</FormField>
									{metodoConfig && (
										<FormField label={metodoConfig.label}>
											<span className={styles.viewText}>{form.metodoValue || '—'}</span>
										</FormField>
									)}
								</div>
								<div className={styles.row2}>
									<FormField label="Forma de aplicação">
										<span className={styles.viewText}>
											{getOptionLabel(APLICACAO_FORMA_OPTIONS, form.forma)}
										</span>
									</FormField>
									{form.forma && (
										<FormField label="Profundidade (m)">
											<span className={styles.viewText}>{form.profundidade || '—'}</span>
										</FormField>
									)}
								</div>
								{showLargura && (
									<div className={showComprimento ? styles.row2 : styles.halfLeft}>
										<FormField label="Largura (m)">
											<span className={styles.viewText}>{form.largura || '—'}</span>
										</FormField>
										{showComprimento && (
											<FormField label="Comprimento (m)">
												<span className={styles.viewText}>{form.comprimento || '—'}</span>
											</FormField>
										)}
									</div>
								)}
							</div>
						</div>

						<div className={styles.section}>
							<FormSectionTitle>Ajuste</FormSectionTitle>
							<div className={styles.sectionFields}>
								<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
									<FormField label="Dose (kg/ha)">
										<span className={styles.viewText}>{ajuste.dose || '—'}</span>
									</FormField>
									{doseSecLabel && (
										<FormField label={doseSecLabel}>
											<span className={styles.viewText}>{ajuste.doseSecundaria || '—'}</span>
										</FormField>
									)}
								</div>
							</div>
						</div>

						{result && (
							<div className={styles.section}>
								<FormSectionTitle>Resultado</FormSectionTitle>
								<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(result.table)} />
							</div>
						)}
					</div>
				</FormCard>

				<PanelActions>
					<FormActions
						mode="view"
						onDelete={
							canEdit
								? () => {
										handleClear();
										setIsSaved(false);
									}
								: undefined
						}
						onEdit={canEdit ? () => setIsSaved(false) : undefined}
					/>
				</PanelActions>
			</div>
		);
	}

	return (
		<div className={styles.panel}>
			<FormCard icon={<CheckCircleOutlined />} title="Calagem">
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Cálculo</FormSectionTitle>
						<div className={styles.sectionFields}>
							<FormField label="Corretivo" required error={errors.corretivo}>
								<FormSelect
									value={form.corretivo}
									onChange={(v) => set('corretivo', v)}
									options={corretivoOptions}
									placeholder="Selecione o corretivo"
									hasError={!!errors.corretivo}
								/>
							</FormField>

							<div className={metodoConfig ? styles.row2 : styles.halfLeft}>
								<FormField label="Método de cálculo" required error={errors.metodo}>
									<FormSelect
										value={form.metodo}
										onChange={(v) => {
											set('metodo', v);
											set('metodoValue', '');
										}}
										options={CALAGEM_METODO_OPTIONS}
										placeholder="Selecione o método"
										hasError={!!errors.metodo}
									/>
								</FormField>
								{metodoConfig && (
									<FormField label={metodoConfig.label} required error={errors.metodoValue}>
										<FormInput
											placeholder={metodoConfig.placeholder}
											type="number"
											value={form.metodoValue}
											onChange={(e) => set('metodoValue', e.target.value)}
											hasError={!!errors.metodoValue}
										/>
									</FormField>
								)}
							</div>

							<div className={styles.row2}>
								<FormField label="Profundidade (m)" required error={errors.profundidade}>
									<FormInput
										placeholder="Ex.: 0.45"
										type="number"
										value={form.profundidade}
										onChange={(e) => set('profundidade', e.target.value)}
										hasError={!!errors.profundidade}
									/>
								</FormField>
								<FormField label="Forma de aplicação" required error={errors.forma}>
									<FormSelect
										value={form.forma}
										onChange={(v) => {
											set('forma', v);
											set('largura', '');
											set('comprimento', '');
										}}
										options={APLICACAO_FORMA_OPTIONS}
										placeholder="Selecione a aplicação"
										hasError={!!errors.forma}
									/>
								</FormField>
							</div>

							{showLargura && (
								<div className={showComprimento ? styles.row2 : styles.halfLeft}>
									<FormField label="Largura (m)" required error={errors.largura}>
										<FormInput
											placeholder="Ex.: 1.4"
											type="number"
											value={form.largura}
											onChange={(e) => set('largura', e.target.value)}
											hasError={!!errors.largura}
										/>
									</FormField>
									{showComprimento && (
										<FormField label="Comprimento (m)" required error={errors.comprimento}>
											<FormInput
												placeholder="Ex.: 0.8"
												type="number"
												value={form.comprimento}
												onChange={(e) => set('comprimento', e.target.value)}
												hasError={!!errors.comprimento}
											/>
										</FormField>
									)}
								</div>
							)}
						</div>
					</div>

					{hasCalculated && (
						<>
							<div className={styles.section}>
								<FormSectionTitle>Ajuste</FormSectionTitle>
								<div className={styles.sectionFields}>
									<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
										<FormField label="Dose (kg/ha)" required>
											<FormInput
												type="number"
												value={ajuste.dose}
												onChange={(e) => setAjuste('dose', e.target.value)}
												onBlur={handleAdjustDose}
												disabled={isAdjusting}
											/>
										</FormField>
										{doseSecLabel && (
											<FormField label={doseSecLabel}>
												<FormInput
													value={ajuste.doseSecundaria}
													readOnly
													className={styles.readonlyInput}
												/>
											</FormField>
										)}
									</div>
								</div>
							</div>

							{result && (
								<div className={styles.section}>
									<FormSectionTitle>Resultado</FormSectionTitle>
									<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(result.table)} />
								</div>
							)}
						</>
					)}
				</div>
			</FormCard>

			<PanelActions>
				<FormActions
					onClear={() => {
						handleClear();
						setIsSaved(false);
					}}
					onSecondary={handleCalculate}
					secondaryText={hasCalculated ? 'Recalcular' : 'Calcular'}
					secondaryDisabled={isCalculating}
					secondaryLoading={isCalculating}
					onSave={handleSaveAndView}
					isLoading={isSaving}
					saveDisabled={!hasCalculated}
				/>
			</PanelActions>
		</div>
	);
};
